import { StudioButton as Button } from './composer/studio/styles';
import { TooltipButton } from '@/ui/TooltipButton';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { Lightbulb, Sparkles, MessageCircle, Image as ImageIcon } from 'lucide-react';
import { StudioTools, type StudioTool } from './composer/studio/StudioTools';
import { emptyExtras, extrasPayload, type ComposerExtras } from './composer/studio/contracts';
import { AuthoredAttachments } from '@/components/PostCard/AuthoredAttachments';
import { Banner, Row as StudioRow, Note } from './composer/studio/styles';
import { useExtrasRecovery, clearExtrasRecovery } from './composer/studio/useExtrasRecovery';
import { errorCopy } from '@/features/networks/service';
import { useDraftRecovery } from './composer/useDraftRecovery';
import { saveRecovery } from './composer/draftRecovery';
// features/home/Composer.tsx — post-composer panel on the Home feed.
//
// Signed-out: shows a CTA linking to /signin.
// Signed-in: avatar + controlled textarea + tools + Post button.
//   Post is disabled when the trimmed value is empty.
//   On submit: calls createPost({ body }) then clears the field.

import { useState, useRef, useMemo, useEffect, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, DisabledHint, MetaRow, Switch, useToast } from '@/ui';
import { ComposerTool as IconButton } from './composer/ComposerTool';
import { Icon } from '@/icons';
import { useSession, usePosts } from '@/hooks';
import { mediaService, uploadErrorMessage } from '@/core/services/mediaService';
import { ComposerFrame as Wrap } from './composer/ComposerFrame';
import { ApiError } from '@/core/lib/apiClient';
import { videoErrorMessage } from '@/core/services/videoUploadService';
import { humanizeFirstFieldError, extractBadHandle } from '@/core/lib/postFieldErrors';
import type { AudienceType, PollDuration } from '@/core/models/post';
import { EmojiPicker } from './composer/EmojiPicker';
import { PollBuilder, MIN_POLL_OPTIONS, MAX_OPTION_LEN } from './composer/PollBuilder';
import { SchedulePicker } from './composer/SchedulePicker';
import { AudienceMenu } from './composer/AudienceMenu';
import { channelDefaultLabel, isVideoOnlyAudience } from './composer/channelDefault';
import { useQuoteDraftStore } from '@/core/stores/quoteDraftStore';
import { useThreadDraftStore } from '@/core/stores/threadDraftStore';
import { QuoteEmbed } from '@/components/PostCard/QuoteEmbed';
import { useComposerVideo } from './composer/useComposerVideo';
import { ComposerVideoChip } from './composer/ComposerVideoChip';
import { ComposerVideoDetails } from './composer/ComposerVideoDetails';
import { ComposerCaptions } from './composer/ComposerCaptions';
import { useComposerCaptions } from './composer/useComposerCaptions';
import { captionsService } from '@/core/services/captionsService';
import { useVideoDetails } from './composer/useVideoDetails';

// ── Styled pieces ─────────────────────────────────────────────────────────────

// Signed-out CTA
const SignedOutInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

// Styled Link that renders as a primary-button to avoid Button's missing `as` prop
const SignInLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  /* Secondary. The gated composer sits directly below the guest banner and
     above the feed's own sign-in prompts, so as an accent fill it was one of
     four identical accent blocks a signed-out visitor met on one screen. */
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  border-color: ${({ theme }) => theme.color.hairSoft};
  transition: background 120ms ease;
`;

const SignedOutText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

// Signed-in composer body
const ComposerBody = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const InputArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  background: transparent;
  border: none;
  padding: 6px 0;
  min-height: 54px;
  resize: none;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.5;
  overflow-wrap: anywhere;
  outline: none;
  box-sizing: border-box;
  &::placeholder {
    color: ${({ theme }) => theme.color.textMuted};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const ToolRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`;

const AudienceSlot = styled.div`
  margin-left: auto;
`;

const PostRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  & > button:first-of-type {
    margin-right: auto;
  }
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  strong {
    display: block;
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
  small {
    color: ${({ theme }) => theme.color.textMuted};
  }
`;
const Counter = styled.div`
  text-align: right;
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;
const MoreMedia = styled.button`
  min-width: 0;
  min-height: 108px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  background: color-mix(in srgb, ${({ theme }) => theme.color.accentCyan} 12%, transparent);
  color: ${({ theme }) => theme.color.textPrimary};
  cursor: pointer;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const ScheduledChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.accentCyan};
  background: ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 4px 8px;
`;

// "Continuing your thread" (1.24.00 C2). Same chip language as the scheduled
// chip, in the muted tone — it states a fact about this draft rather than
// advertising a feature.
const ThreadChip = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  margin-top: 4px;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ChipRemoveBtn = styled(TooltipButton)`
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  padding: 0;
`;

/** Matches the server's MAX_POST_IMAGES cap. */
const MAX_POST_IMAGES = 4;

const PreviewStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px;
  margin-top: 4px;
`;

const PreviewChip = styled.div`
  position: relative;
  min-width: 0;
`;

const PreviewThumb = styled.img`
  width: 100%;
  height: 132px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const RemoveFileBtn = styled(TooltipButton)`
  position: absolute;
  top: 6px;
  right: 6px;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  background: ${({ theme }) => theme.color.surface};
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const ErrorLine = styled.p`
  margin: 4px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.danger};
`;

const HintLine = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

// Non-editable, non-navigating quote-draft preview (1.21.00 Slice 2). Unlike
// QuoteEmbed (used on published posts, which IS a link to the quoted post),
// this must never navigate away from an in-progress draft — it's a plain
// wrapper with its own cancel × rather than a Link.
const QuotePreviewWrap = styled.div`
  position: relative;
  margin-top: 4px;
`;

const QuotePreviewRemove = styled(TooltipButton)`
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textPrimary};
  cursor: pointer;
`;

// ── Component ─────────────────────────────────────────────────────────────────

export interface ComposerProps {
  initialPerspective?: import('./composer/studio/contracts').StudioResource;
  recoverText?: boolean;
  overlay?: boolean;
  compact?: boolean;
  onStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
  onComplete?: () => void;
}
export function Composer(props: ComposerProps) {
  const { currentUser } = useSession();
  return <AccountComposer key={currentUser?.id ?? 'guest'} {...props} />;
}
function AccountComposer({
  initialPerspective,
  recoverText = false,
  overlay = false,
  compact = false,
  onStateChange,
  onComplete,
}: ComposerProps) {
  const { isAuthenticated, isEmailVerified, currentUser } = useSession();
  const { createPost } = usePosts();
  const toast = useToast();
  const [value, setValue] = useState('');
  const recoveryOwner = currentUser?.id ?? 'guest';
  const recovery = useDraftRecovery(recoveryOwner, recoverText, value, setValue);
  const [files, setFiles] = useState<File[]>([]);
  // Video phase 2: at most one clip, instead of images and never with a poll.
  const video = useComposerVideo(isAuthenticated && isEmailVerified);
  const videoInputRef = useRef<HTMLInputElement>(null);
  // The attached clip's title / category / Short switch (Videos page, 2026-09-26).
  const videoDetails = useVideoDetails(
    video.current?.file ?? null,
    video.current?.durationMs ?? null,
    video.current?.portrait ?? null,
    value,
    currentUser?.autoShortClassifier ?? true,
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [extras, setExtras] = useState<ComposerExtras>(() => ({
    ...emptyExtras(),
    perspective: initialPerspective,
  }));
  const extraRecovery = useExtrasRecovery(recoveryOwner, recoverText, extras, (next) => {
    setExtras(next);
    if (next.collaboration) setValue(next.collaboration.body);
  });
  const [studioTool, setStudioTool] = useState<StudioTool | null>(null);
  const studioTrigger = useRef<HTMLButtonElement>(null);
  function closeStudio(tool: StudioTool | null) {
    setStudioTool(tool);
    if (!tool) requestAnimationFrame(() => studioTrigger.current?.focus());
  }
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Poll builder — mutually exclusive with images.
  const [pollOpen, setPollOpen] = useState(false);
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [pollDuration, setPollDuration] = useState<PollDuration>('24h');

  // Schedule.
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);

  // Captions for the attached clip (1.27.60) — the uploader's file, or made on this device.
  const composerCaptions = useComposerCaptions(video.current?.file ?? null);

  // Audience. 'public' / 'unlisted' only exist while a video is attached — removing the
  // video falls back to the default rather than sending a choice the server would refuse.
  const [pickedAudience, setAudience] = useState<AudienceType>('everyone');
  const audience: AudienceType =
    !video.current && isVideoOnlyAudience(pickedAudience) ? 'everyone' : pickedAudience;
  const videoAudience = video.current
    ? { defaultLabel: channelDefaultLabel(currentUser) }
    : undefined;
  const [regions, setRegions] = useState<string[]>([]);
  const [people, setPeople] = useState<string[]>([]);
  const [badHandle, setBadHandle] = useState<string | null>(null);

  // Quote posts (1.21.00 Slice 2) — picked up from the cross-page draft store
  // (see PostActions.tsx's "Quote" item + core/stores/quoteDraftStore.ts).
  //
  // W7 (1.21.00 review): the original useEffect-based version called setState
  // synchronously inside an effect body (a lint error, and an extra render).
  // A plain lazy useState initializer isn't a full fix here, though, the way
  // it is in mobile's ComposeModal — THIS Composer instance is not always
  // freshly mounted per open: on desktop it lives inline on Home and stays
  // mounted continuously, so tapping "Quote" on another card while already on
  // Home would set a new pendingQuote that a lazy initializer (which only
  // ever runs once, at first mount) would never see.
  //
  // Instead this uses React's documented "adjust state during rendering"
  // pattern: tracking the last-seen store value in STATE (not a ref — this
  // codebase's lint config runs the React Compiler-aware
  // react-hooks/refs rule, which forbids reading/writing a ref's `.current`
  // during render, even for this otherwise-standard pattern) and calling
  // setState synchronously in the render body itself (not in an effect) when
  // it changes. This is React-sanctioned (see "You Might Not Need an Effect"
  // → adjusting state when a prop changes) and handles both this component's
  // mount shapes — the always-mounted desktop inline instance AND the
  // freshly-mounted-per-open Sheet instance (mobile FAB / desktop Topbar
  // "Create") — with one code path.
  const pendingQuote = useQuoteDraftStore((s) => s.pendingQuote);
  const clearPendingQuote = useQuoteDraftStore((s) => s.clearPendingQuote);
  const [quotedPost, setQuotedPost] = useState<typeof pendingQuote>(null);
  const [lastConsumedPendingQuote, setLastConsumedPendingQuote] =
    useState<typeof pendingQuote>(null);

  // Threads (1.24.00 C2) — same one-shot handoff as the quote draft above, and
  // adjusted during render for the same reason (the desktop inline Composer
  // stays mounted, so a lazy initializer would never see a second "Add to
  // thread" tap).
  //
  // The OTHER server rule — a part inherits the root's audience — is handled at
  // exactly one place, where the request body is built. The audience the member
  // picked is left alone here on purpose: dismiss the chip and their choice is
  // still there, because it was never quietly rewritten.
  const pendingThreadRoot = useThreadDraftStore((s) => s.pendingThreadRoot);
  const clearPendingThreadRoot = useThreadDraftStore((s) => s.clearPendingThreadRoot);
  const [threadRoot, setThreadRoot] = useState<typeof pendingThreadRoot>(null);
  const [lastConsumedThreadRoot, setLastConsumedThreadRoot] =
    useState<typeof pendingThreadRoot>(null);

  // A post cannot be BOTH a thread part and a quote — the server answers
  // 400 `thread_and_quote`, which reaches the member as a generic "could not
  // post" they have no way to act on. So the composer resolves it here instead:
  // whichever of the two is staged LAST wins, the other is dropped, and this
  // note says which, because silently discarding something somebody staged is
  // its own small betrayal.
  //
  // 1.24.00 C3 fix round: only the thread→quote direction existed. Staging a
  // quote on top of a thread root left BOTH set and the post just failed.
  const [exclusiveNote, setExclusiveNote] = useState<string | null>(null);

  if (pendingQuote && pendingQuote !== lastConsumedPendingQuote) {
    setLastConsumedPendingQuote(pendingQuote);
    setQuotedPost(pendingQuote);
    if (threadRoot) {
      setThreadRoot(null);
      setExclusiveNote(
        'Quoting a post, so this one left your thread. A post can do one or the other, not both.',
      );
    }
    clearPendingQuote();
  }

  // W8 (1.21.00 review), second half: quoteDraftStore.pendingQuote is cleared
  // SYNCHRONOUSLY the instant any composer instance renders it (immediately
  // above — not on a delayed effect tick), so there is never a window where
  // a stale draft sits in the store waiting to attach to something
  // unrelated. If the user closes this composer without posting, `quotedPost`
  // (this instance's own local state) simply unmounts with it, and the next
  // "Quote" tap writes a brand-new value the next composer's render picks up.

  if (pendingThreadRoot && pendingThreadRoot !== lastConsumedThreadRoot) {
    setLastConsumedThreadRoot(pendingThreadRoot);
    setThreadRoot(pendingThreadRoot);
    if (extras.ring) setExtras({ ...extras, ring: undefined });
    // `pendingQuote` as well as the committed state: when BOTH arrive before
    // this composer's first render (mobile's freshly-mounted sheet), the quote
    // was consumed into `quotedPost` a few lines up and is about to be dropped
    // here, so reading only the state of the previous render would miss it.
    if (quotedPost || pendingQuote) {
      setExclusiveNote(
        'Continuing your thread, so the quoted post was removed. A post can do one or the other, not both.',
      );
    }
    setQuotedPost(null);
    clearPendingThreadRoot();
  }

  // Cache successful uploads by File identity so a retry after a validation-400
  // (which only happens once every image has already uploaded) doesn't re-upload
  // them. Pruned whenever the file selection changes, so a removed-then-re-added
  // file never silently reuses a stale result.
  const uploadCacheRef = useRef<Map<File, { key: string; width?: number; height?: number }>>(
    new Map(),
  );
  useEffect(() => {
    const current = new Set(files);
    for (const f of uploadCacheRef.current.keys()) {
      if (!current.has(f)) uploadCacheRef.current.delete(f);
    }
  }, [files]);

  // One object URL per selected file, revoked together when the selection changes so a
  // long composing session cannot leak blobs.
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);

  const dirty = Boolean(
    value.trim() ||
    files.length ||
    pollOpen ||
    scheduledAt ||
    quotedPost ||
    threadRoot ||
    studioTool ||
    Object.keys(extras.attachments).length ||
    extras.perspective ||
    extras.collaboration ||
    extras.ring ||
    extras.question,
  );
  useEffect(() => {
    onStateChange?.({ dirty, busy });
  }, [dirty, busy, onStateChange]);

  if (!isAuthenticated) {
    return (
      <Wrap data-testid="composer">
        <SignedOutInner>
          <SignedOutText>Sign in to share your voice on TruXSocial.</SignedOutText>
          <SignInLink to="/signin">Sign in</SignInLink>
        </SignedOutInner>
      </Wrap>
    );
  }

  if (!isEmailVerified) {
    const gatedName = currentUser?.name ?? 'User';
    return (
      <Wrap data-testid="composer">
        {recovery.pending && (
          <div role="status">
            <p>Unfinished post found. Recover the text from this browser?</p>
            <p>Text only: check your audience and reattach media or polls before posting.</p>
            <Button onClick={recovery.restore}>Restore text</Button>
            <Button variant="ghost" onClick={recovery.discard}>
              Discard recovered text
            </Button>
          </div>
        )}
        <ComposerBody>
          <Avatar name={gatedName} size={44} avatarKey={currentUser?.avatarKey} />
          <InputArea>
            <StyledTextarea
              value=""
              readOnly
              disabled
              placeholder="Verify your email to post. Check your inbox for the link."
              aria-label="Compose post (verify your email to enable posting)"
              rows={3}
            />
            <ToolRow>
              <IconButton icon="image" label="Add image" size={18} disabled tabIndex={-1} />
              <IconButton icon="bar-chart-3" label="Add poll" size={18} disabled tabIndex={-1} />
              <IconButton icon="smile" label="Add emoji" size={18} disabled tabIndex={-1} />
              <IconButton
                icon="calendar-clock"
                label="Schedule post"
                size={18}
                disabled
                tabIndex={-1}
              />
              <IconButton
                icon="globe"
                label="Post audience: Everyone"
                size={18}
                disabled
                tabIndex={-1}
              />
            </ToolRow>
          </InputArea>
        </ComposerBody>
        <PostRow>
          <Button variant="primary" size="sm" disabled>
            Post
          </Button>
        </PostRow>
      </Wrap>
    );
  }

  const userName = currentUser?.name ?? 'User';
  const trimmed = value.trim();

  // Poll validity: 2–4 non-empty options ≤80 chars each (mirrors the server rule).
  const trimmedPollOptions = pollOptions.map((o) => o.trim());
  const pollValid =
    !pollOpen ||
    (trimmedPollOptions.length >= MIN_POLL_OPTIONS &&
      trimmedPollOptions.every((o) => o.length > 0 && o.length <= MAX_OPTION_LEN));

  // A poll IS content — a poll-only post (no body text) is allowed, same as mobile.
  const hasBody = trimmed.length > 0;
  const videoReady = video.current?.stage === 'ready';
  const hasContent = hasBody || (pollOpen && pollValid) || videoReady;

  // Audience must name at least one region/person once that type is picked — an
  // empty list 400s server-side with the literal word "required".
  const audienceValid =
    Boolean(threadRoot || extras.ring) ||
    ((audience !== 'regions' || regions.length > 0) &&
      (audience !== 'people' || people.length > 0));
  const audienceHint =
    audience === 'regions' && regions.length === 0
      ? 'Pick at least one region.'
      : audience === 'people' && people.length === 0
        ? 'Pick at least one person.'
        : null;

  const canSaveDraft =
    !recovery.pending &&
    !extraRecovery.pending &&
    hasContent &&
    Array.from(value).length <= 5000 &&
    !busy &&
    // A clip still compressing/uploading (or one that failed) holds the post back.
    !video.busy &&
    // …and so do captions still being made (cancel them to post now).
    !composerCaptions.busy &&
    !studioTool &&
    (!extras.question || hasBody) &&
    pollValid &&
    audienceValid;
  const canSubmit = canSaveDraft && (!scheduleEnabled || Boolean(scheduledAt));

  function resetComposer() {
    if (recoverText) clearExtrasRecovery(recoveryOwner);
    setExtras(emptyExtras());
    setStudioTool(null);
    if (recoverText) saveRecovery(recoveryOwner, '');
    setValue('');
    setFiles([]);
    video.reset();
    setPollOpen(false);
    setPollOptions(['', '']);
    setPollDuration('24h');
    setScheduledAt(null);
    setScheduleEnabled(false);
    setAudience('everyone');
    setRegions([]);
    setPeople([]);
    setBadHandle(null);
    setQuotedPost(null);
    setThreadRoot(null);
    setExclusiveNote(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function handlePost(saveAsDraft = false) {
    if (!(saveAsDraft && overlay ? canSaveDraft : canSubmit)) return;
    setBusy(true);
    setErr(null);
    setBadHandle(null);
    try {
      // Upload sequentially: four large photos in parallel on a slow connection stall the
      // request queue and can trip the server's upload rate limit.
      const images: { key: string; width?: number; height?: number }[] = [];
      if (!pollOpen) {
        for (const f of files) {
          const cached = uploadCacheRef.current.get(f);
          if (cached) {
            images.push(cached);
            continue;
          }
          const up = await mediaService.upload('post', f);
          // Keep the dimensions the server already decoded — they let the feed reserve the
          // exact box before the image loads, so nothing shifts as you scroll.
          const result = {
            key: up.key,
            ...(up.width ? { width: up.width } : {}),
            ...(up.height ? { height: up.height } : {}),
          };
          uploadCacheRef.current.set(f, result);
          images.push(result);
        }
      }
      const wasScheduled = !saveAsDraft && scheduledAt !== null;
      const scheduledForLabel = scheduledLabel;
      // Captions go onto the clip first, so the post arrives with them. A failure never
      // costs the post itself.
      const captionUpload = video.current?.video ? composerCaptions.upload() : null;
      if (captionUpload && video.current?.video) {
        try {
          await captionsService.upload(video.current.video.id, captionUpload);
        } catch {
          toast(
            'Your captions couldn’t be added. You can add them from Video settings in the video’s three-dot menu.',
          );
        }
      }
      await createPost({
        body: extras.collaboration ? value : trimmed,
        ...extrasPayload(extras, Boolean(threadRoot)),
        ...(images.length ? { images } : {}),
        ...(video.current?.video
          ? {
              videoId: video.current.video.id,
              videoTitle: videoDetails.details.title.trim(),
              videoCategory: videoDetails.details.category,
              videoShort: videoDetails.details.short,
              videoAllowDownload: videoDetails.details.allowDownload,
              videoAllowEmbed: videoDetails.details.allowEmbed,
              videoAllowClips: videoDetails.details.allowClips,
            }
          : {}),
        ...(pollOpen ? { poll: { options: trimmedPollOptions, duration: pollDuration } } : {}),
        // A draft is never scheduled — the two are mutually exclusive
        // server-side (see PostViewSet.create's draft/scheduledAt conflict
        // check) — saveAsDraft always wins if somehow both were set.
        ...(saveAsDraft ? { draft: true } : scheduledAt ? { scheduledAt } : {}),
        // A thread part inherits the root's audience — sending ANY audience key
        // alongside threadRootId is a 400 (`inherits_audience`), so the whole
        // audience group is omitted rather than trusted to be 'everyone'.
        ...(threadRoot || extras.ring
          ? {}
          : {
              ...(audience !== 'everyone' ? { audience } : {}),
              ...(audience === 'regions' && regions.length ? { regions } : {}),
              ...(audience === 'people' && people.length ? { people } : {}),
            }),
        ...(quotedPost ? { quotedPostId: quotedPost.shareCode ?? quotedPost.id } : {}),
        ...(threadRoot ? { threadRootId: threadRoot.shareCode ?? threadRoot.id } : {}),
      });
      resetComposer();
      // Scheduling/drafting have no other confirmation — the composer just
      // clears, which reads as "did that work?" without an explicit toast.
      if (wasScheduled) toast(`Scheduled for ${scheduledForLabel}`);
      else if (saveAsDraft) toast('Saved as a draft');
      // A published post needs the same answer: the sheet closing on its own is not
      // a confirmation, least of all when the member is not on the Home feed.
      else toast('Posted. It is at the top of your Following feed.');
      onComplete?.();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'video_daily_limit') {
        setErr(videoErrorMessage(e));
      } else if (e instanceof ApiError && e.code === 'validation') {
        const fields = e.fields ?? {};
        // Highlight the offending handle in the audience menu when the server names one.
        const badHandleFound = audience === 'people' ? extractBadHandle(fields) : null;
        if (badHandleFound) setBadHandle(badHandleFound);
        // A named-handle 400 gets its own composer-level message — not buried
        // only inside the audience menu's people view two taps deep.
        setErr(
          badHandleFound
            ? `${badHandleFound} isn't a TruXSocial account. Remove or fix it.`
            : humanizeFirstFieldError(fields),
        );
      } else if (
        e instanceof ApiError &&
        [
          'revision_conflict',
          'collaboration_changed',
          'collaborators_pending',
          'audience_ring_empty',
          'audience_ring_changed',
          'audience_ring_members_unavailable',
          'perspective_invitations_pending',
          'perspective_needs_two_viewpoints',
          'perspective_already_attached',
          'collaboration_already_attached',
          'feature_disabled',
        ].includes(e.code)
      ) {
        setErr(errorCopy(e));
      } else {
        setErr(uploadErrorMessage(e, 'Could not post. Please try again.'));
      }
    } finally {
      setBusy(false);
    }
  }

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setValue(e.target.value);
  }

  /** Insert an emoji at the current caret position (or replace the selection). */
  function insertEmoji(emoji: string) {
    if (extras.collaboration) {
      setErr('Open your collaborative draft to edit its shared text, or detach it first.');
      return;
    }
    const el = textareaRef.current;
    if (!el) {
      setValue((v) => v + emoji);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + emoji + value.slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
    });
  }

  function togglePoll() {
    if (pollOpen) {
      setPollOpen(false);
      return;
    }
    setFiles([]);
    if (inputRef.current) inputRef.current.value = '';
    setPollOpen(true);
  }

  function attachVideo(file: File) {
    if (busy || !video.available) return;
    if (!video.canUpload) {
      setErr('Turn on two-factor authentication in Settings to post videos.');
      return;
    }
    if (pollOpen || files.length > 0) {
      setErr("A video can't be combined with images or a poll. Remove them first.");
      return;
    }
    setErr(null);
    video.attach(file);
  }

  function openVideoPicker() {
    if (busy || pollOpen || files.length > 0 || !videoInputRef.current) return;
    videoInputRef.current.click();
  }

  function addFiles(picked: File[]) {
    if (busy) return;
    // A dropped clip goes to the video slot, not the image strip.
    const droppedVideo = picked.find((f) => f.type.startsWith('video/'));
    if (droppedVideo && video.available) {
      attachVideo(droppedVideo);
      return;
    }
    if (video.current) {
      setErr("A video can't be combined with images. Remove the video first.");
      return;
    }
    if (pollOpen) {
      setErr("A poll can't be combined with images. Remove the poll first.");
      return;
    }
    const valid = picked.filter((file) =>
      ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type),
    );
    setErr(
      valid.length !== picked.length
        ? 'Choose JPEG, PNG, WebP or GIF images.'
        : files.length + valid.length > MAX_POST_IMAGES
          ? `You can attach up to ${MAX_POST_IMAGES} images.`
          : null,
    );
    setFiles((current) => [...current, ...valid].slice(0, MAX_POST_IMAGES));
  }

  function openMediaPicker(gifOnly = false) {
    if (busy || pollOpen || video.current || !inputRef.current) return;
    inputRef.current.accept = gifOnly ? 'image/gif' : 'image/jpeg,image/png,image/webp,image/gif';
    inputRef.current.click();
  }

  const scheduledLabel = scheduledAt
    ? new Date(scheduledAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  return (
    <Wrap
      data-testid="composer"
      $overlay={overlay}
      data-overlay={overlay}
      aria-busy={busy}
      inert={busy}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        addFiles(Array.from(e.dataTransfer.files));
      }}
    >
      {overlay && (
        <>
          <Note>Share your ideas, updates, and what moves you.</Note>
          <Banner>
            <Lightbulb size={36} aria-hidden="true" />
            <div>
              <strong>Ideas drive change.</strong>
              <Note>Share your thoughts. Inspire your community.</Note>
            </div>
          </Banner>
        </>
      )}
      <NetworkLinks destination="creator" onCollaborate={() => setStudioTool('collaboration')} />
      {overlay && (
        <Identity data-identity>
          <Avatar name={userName} size={44} avatarKey={currentUser?.avatarKey} />
          <div>
            <strong>{userName}</strong>
            <small>{currentUser?.handle}</small>
          </div>
          <AudienceSlot>
            {extras.ring && !threadRoot && (
              <Button onClick={() => setStudioTool('audience')}>{extras.ring.name}</Button>
            )}
            {!threadRoot && !extras.ring && (
              <AudienceMenu
                audience={audience}
                regions={regions}
                people={people}
                badHandle={badHandle}
                video={videoAudience}
                onChange={(next, nextRegions, nextPeople) => {
                  setExtras((current) => ({ ...current, ring: undefined }));
                  setAudience(next);
                  setRegions(next === 'regions' ? nextRegions : []);
                  setPeople(next === 'people' ? nextPeople : []);
                  setBadHandle(null);
                }}
              />
            )}
          </AudienceSlot>
        </Identity>
      )}
      {studioTool && (
        <StudioTools
          key={recoveryOwner}
          tool={studioTool}
          setTool={closeStudio}
          owner={recoveryOwner}
          body={value}
          extras={extras}
          onChange={(next) => {
            setExtras(next);
            if (next.collaboration && next.collaboration !== extras.collaboration)
              setValue(next.collaboration.body);
            if (next.ring && threadRoot) {
              setThreadRoot(null);
              setExclusiveNote('Posting to your selected ring as a standalone post.');
            }
          }}
          onInsert={insertEmoji}
          onGif={() => openMediaPicker(true)}
          onThread={(post) => {
            setThreadRoot(post);
            setQuotedPost(null);
            setExtras((current) => ({ ...current, ring: undefined }));
            setExclusiveNote('Continuing the original timeline with its existing audience.');
          }}
        />
      )}
      <StudioRow aria-label="Post mode">
        <Button
          aria-pressed={!extras.question && files.length === 0}
          onClick={() => setExtras({ ...extras, question: false })}
        >
          <Sparkles size={17} />
          Share an update
        </Button>
        <Button
          aria-pressed={extras.question}
          onClick={() => setExtras({ ...extras, question: true })}
        >
          <MessageCircle size={17} />
          Ask a question
        </Button>
        <Button
          aria-pressed={!extras.question && files.length > 0}
          disabled={pollOpen || busy}
          onClick={() => {
            setExtras({ ...extras, question: false });
            openMediaPicker();
          }}
        >
          <ImageIcon size={17} />
          Share media
        </Button>
      </StudioRow>
      {extras.question && (
        <Note>Replies can be marked helpful using the existing question tools.</Note>
      )}
      <StudioRow>
        {extras.ring && (
          <>
            <Note>
              <MetaRow>
                {`Audience: ${extras.ring.name}`}
                {extras.ring.people.map((p) => '@' + p.handle.replace(/^@/, '')).join(', ')}
              </MetaRow>
            </Note>
            <Button onClick={() => setStudioTool('audience')}>Review ring</Button>
            <Button
              variant="ghost"
              onClick={() => {
                setExtras({ ...extras, ring: undefined });
                setAudience('everyone');
                setPeople([]);
                setRegions([]);
              }}
            >
              Remove ring
            </Button>
          </>
        )}
        {extras.collaboration && (
          <>
            <Note>Shared draft: {extras.collaboration.title}</Note>
            <Button onClick={() => setStudioTool('collaboration')}>Edit shared draft</Button>
            <Button
              variant="ghost"
              onClick={() => {
                setExtras({ ...extras, collaboration: undefined });
                setValue('');
              }}
            >
              Detach shared draft
            </Button>
          </>
        )}
        {extras.perspective && (
          <>
            <Note>Perspectives: {extras.perspective.title}</Note>
            <Button onClick={() => setStudioTool('perspective')}>Review perspectives</Button>
            <Button
              variant="ghost"
              onClick={() => setExtras({ ...extras, perspective: undefined })}
            >
              Remove perspectives
            </Button>
          </>
        )}
      </StudioRow>
      {(recovery.pending || extraRecovery.pending) && (
        <div role="status">
          <p>Unfinished post found. Recover the text from this browser?</p>
          <p>
            Recover your text and authored attachments. Review audience and shared drafts, and
            reattach media or polls before posting.
          </p>
          <Button
            onClick={() => {
              recovery.restore();
              extraRecovery.restore();
            }}
          >
            Restore text
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              recovery.discard();
              extraRecovery.discard();
            }}
          >
            Discard recovered text
          </Button>
        </div>
      )}
      <ComposerBody>
        {!overlay && <Avatar name={userName} size={40} avatarKey={currentUser?.avatarKey} />}
        <InputArea>
          <StyledTextarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            placeholder={extras.question ? 'What would you like to ask?' : "What's on your mind?"}
            aria-label="Compose post"
            rows={overlay ? 5 : 2}
            disabled={busy || Boolean(recovery.pending)}
            readOnly={Boolean(extras.collaboration)}
          />
          {(overlay || value.length > 4500) && (
            <Counter>{Array.from(value).length.toLocaleString()}/5,000</Counter>
          )}
          <AuthoredAttachments
            attachments={extras.attachments}
            perspectives={
              extras.perspective
                ? {
                    slug: extras.perspective.slug,
                    title: extras.perspective.title,
                    commonGround: extras.perspective.data.commonGround ?? '',
                    viewpoints: extras.perspective.data.viewpoints ?? [],
                  }
                : undefined
            }
          />

          {/* Hidden file input triggered by the Add image button */}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            data-testid="composer-file-input"
            hidden
            disabled={pollOpen}
            onChange={(e) => {
              setErr(null);
              // Defensive: the trigger button is disabled while a poll is open, but a
              // file dialog opened just before the poll toggle can still resolve after
              // it — never silently drop the poll in favor of images that slipped in.
              if (pollOpen) {
                setErr("A poll can't be combined with images. Remove the poll first.");
                if (inputRef.current) inputRef.current.value = '';
                return;
              }
              addFiles(Array.from(e.target.files ?? []));
              // Reset so re-picking the same file still fires a change event.
              if (inputRef.current) inputRef.current.value = '';
            }}
          />
          {video.available && (
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm,video/*"
              data-testid="composer-video-input"
              hidden
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (picked) attachVideo(picked);
                if (videoInputRef.current) videoInputRef.current.value = '';
              }}
            />
          )}

          {/* Thread-vs-quote exclusivity, said out loud (1.24.00 C3 fix round).
              role="status" so it is announced without stealing focus — the
              member did not ask for an error, they just staged the other one. */}
          {exclusiveNote && (
            <HintLine role="status" data-testid="composer-exclusive-note">
              {exclusiveNote}
            </HintLine>
          )}

          {/* Thread chip (1.24.00 C2) — states what this draft will become, and
              can be dismissed to post it as a stand-alone post instead. */}
          {threadRoot && (
            <ThreadChip data-testid="composer-thread-chip">
              <Icon name="list-ordered" size={12} />
              Continuing your thread
              <ChipRemoveBtn
                type="button"
                aria-label="Post on its own instead"
                onClick={() => {
                  setThreadRoot(null);
                  setExclusiveNote(null);
                }}
              >
                <Icon name="close" size={12} />
              </ChipRemoveBtn>
            </ThreadChip>
          )}

          {/* Quote-draft preview (1.21.00 Slice 2) — non-editable, no navigation. */}
          {quotedPost && (
            <QuotePreviewWrap>
              <QuoteEmbed
                quotedPost={{
                  id: quotedPost.id,
                  authorId: quotedPost.authorId,
                  name: quotedPost.name,
                  handle: quotedPost.handle,
                  av: quotedPost.av,
                  verified: quotedPost.verified,
                  time: quotedPost.time,
                  createdAt: quotedPost.createdAt ?? '',
                  body: quotedPost.body,
                  images: quotedPost.images,
                  shareCode: quotedPost.shareCode,
                  hasQuote: Boolean(quotedPost.quotedPost),
                }}
                onClick={(e) => e.preventDefault()}
              />
              <QuotePreviewRemove
                type="button"
                aria-label="Remove quoted post"
                onClick={() => {
                  setQuotedPost(null);
                  setExclusiveNote(null);
                }}
              >
                <Icon name="close" size={12} />
              </QuotePreviewRemove>
            </QuotePreviewWrap>
          )}

          {/* Selected-image strip — one chip per attachment, each removable */}
          {!pollOpen && files.length > 0 && (
            <PreviewStrip data-testid="composer-preview-strip">
              {files.map((f, i) => (
                <PreviewChip key={`${f.name}-${f.lastModified}-${i}`}>
                  <PreviewThumb src={previews[i]} alt={f.name} />
                  <RemoveFileBtn
                    type="button"
                    aria-label={`Remove ${f.name}`}
                    disabled={busy || Boolean(recovery.pending)}
                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <Icon name="close" size={16} />
                  </RemoveFileBtn>
                </PreviewChip>
              ))}
              {files.length < MAX_POST_IMAGES && (
                <MoreMedia type="button" disabled={busy} onClick={() => openMediaPicker()}>
                  <Icon name="plus" size={22} />
                  Add more<small>Drop images or click</small>
                </MoreMedia>
              )}
            </PreviewStrip>
          )}

          {video.current && (
            <ComposerVideoChip
              video={video.current}
              onRemove={video.remove}
              disabled={busy || Boolean(recovery.pending)}
            />
          )}
          {video.current && video.current.stage !== 'error' && (
            <ComposerVideoDetails
              details={videoDetails.details}
              canBeShort={videoDetails.canBeShort}
              onTitle={videoDetails.setTitle}
              onCategory={videoDetails.setCategory}
              onShort={videoDetails.setShort}
              onPermission={videoDetails.setPermission}
              disabled={busy}
            />
          )}
          {video.current && video.current.stage !== 'error' && (
            <ComposerCaptions captions={composerCaptions} disabled={busy} />
          )}

          {/* Poll builder — swaps in for the media zone. Mutually exclusive with images. */}
          {pollOpen && (
            <PollBuilder
              options={pollOptions}
              duration={pollDuration}
              onOptionsChange={setPollOptions}
              onDurationChange={setPollDuration}
              onRemove={() => setPollOpen(false)}
            />
          )}
          {pollOpen && <HintLine>A poll can&apos;t be combined with images.</HintLine>}
          {!pollOpen && files.length > 0 && <HintLine>Remove your images to add a poll.</HintLine>}

          {/* Inline upload/post error */}
          {err && <ErrorLine role="alert">{err}</ErrorLine>}
          {!err && audienceHint && <ErrorLine role="alert">{audienceHint}</ErrorLine>}

          <ToolRow data-tools>
            {/* Add image — triggers file picker. Disabled while building a poll or with a video. */}
            {pollOpen || video.current ? (
              <DisabledHint
                explanation={
                  video.current
                    ? 'Remove your video to add images.'
                    : "A poll can't be combined with images."
                }
              >
                {(describedBy) => (
                  <IconButton
                    icon="image"
                    label="Add image"
                    size={18}
                    aria-disabled="true"
                    aria-describedby={describedBy}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                )}
              </DisabledHint>
            ) : (
              <IconButton
                icon="image"
                label="Add image"
                size={18}
                disabled={busy || Boolean(recovery.pending)}
                onClick={() => openMediaPicker()}
              />
            )}
            {/* Add video — only once the owner has switched video on. Needs 2FA. */}
            {video.available &&
              (!video.canUpload || pollOpen || files.length > 0 ? (
                <DisabledHint
                  explanation={
                    !video.canUpload
                      ? 'Turn on two-factor authentication in Settings to post videos.'
                      : "A video can't be combined with images or a poll."
                  }
                >
                  {(describedBy) => (
                    <IconButton
                      icon="video"
                      label="Add video"
                      size={18}
                      aria-disabled="true"
                      aria-describedby={describedBy}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    />
                  )}
                </DisabledHint>
              ) : (
                <IconButton
                  icon="video"
                  label="Add video"
                  size={18}
                  disabled={busy || Boolean(recovery.pending)}
                  onClick={openVideoPicker}
                />
              ))}
            {/* Add poll — disabled once images or a video are attached. */}
            {files.length > 0 || video.current ? (
              <DisabledHint
                explanation={
                  video.current
                    ? 'Remove your video to add a poll.'
                    : 'Remove your images to add a poll.'
                }
              >
                {(describedBy) => (
                  <IconButton
                    icon="bar-chart-3"
                    label="Add poll"
                    size={18}
                    aria-disabled="true"
                    aria-describedby={describedBy}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                )}
              </DisabledHint>
            ) : (
              <IconButton
                icon="bar-chart-3"
                label={pollOpen ? 'Remove poll' : 'Add poll'}
                size={18}
                aria-pressed={pollOpen}
                onClick={togglePoll}
              />
            )}
            <EmojiPicker onSelect={insertEmoji} />
            {overlay ? (
              <IconButton
                icon="calendar-clock"
                label="Schedule post"
                onClick={() => {
                  if (scheduleEnabled) setScheduledAt(null);
                  setScheduleEnabled((on) => !on);
                }}
              />
            ) : (
              <SchedulePicker onSchedule={setScheduledAt} />
            )}
            <IconButton icon="link" label="Add link" onClick={() => setStudioTool('link')} />
            <Button
              ref={studioTrigger}
              data-composer-tool
              aria-expanded={Boolean(studioTool)}
              onClick={() => (studioTool ? closeStudio(null) : setStudioTool('more'))}
            >
              <Icon name="more-horizontal" size={20} />
              More
            </Button>

            {/* A thread part inherits the root's audience, so there is nothing
                to choose here — the control is removed rather than shown as a
                dead one, and the line below says why. */}
            {!overlay && (
              <AudienceSlot>
                {threadRoot ? (
                  <HintLine>Follows the audience of the post you&apos;re continuing.</HintLine>
                ) : extras.ring ? (
                  <Button onClick={() => setStudioTool('audience')}>{extras.ring.name}</Button>
                ) : (
                  <AudienceMenu
                    audience={audience}
                    regions={regions}
                    people={people}
                    badHandle={badHandle}
                    video={videoAudience}
                    onChange={(nextAudience, nextRegions, nextPeople) => {
                      setExtras((current) => ({ ...current, ring: undefined }));
                      setAudience(nextAudience);
                      // Switching type CLEARS the other type's selections — a stale list
                      // must never silently ride along on a later submit.
                      setRegions(nextAudience === 'regions' ? nextRegions : []);
                      setPeople(nextAudience === 'people' ? nextPeople : []);
                      setBadHandle(null);
                    }}
                  />
                )}
              </AudienceSlot>
            )}
            {compact && (
              <Button
                variant="primary"
                size="sm"
                disabled={!canSubmit}
                onClick={() => void handlePost()}
              >
                {scheduledAt ? 'Schedule' : 'Post'}
              </Button>
            )}
          </ToolRow>
        </InputArea>
      </ComposerBody>
      {overlay && (
        <ToolRow>
          <Icon name="calendar-clock" size={18} />
          <HintLine>Schedule for later</HintLine>
          <AudienceSlot>
            <Switch
              checked={scheduleEnabled || Boolean(scheduledAt)}
              label="Schedule for later"
              onChange={(on) => {
                setScheduleEnabled(on);
                if (!on) setScheduledAt(null);
              }}
            />
          </AudienceSlot>
        </ToolRow>
      )}
      {overlay && scheduleEnabled && (
        <SchedulePicker inline onSchedule={setScheduledAt} onEdit={() => setScheduledAt(null)} />
      )}
      {overlay && threadRoot && (
        <HintLine>Follows the audience of the post you&apos;re continuing.</HintLine>
      )}

      {(!compact || dirty) && (
        <PostRow>
          {scheduledLabel && (
            <ScheduledChip data-testid="composer-scheduled-chip">
              Scheduled for {scheduledLabel}
              <ChipRemoveBtn
                type="button"
                aria-label="Remove schedule"
                onClick={() => {
                  setScheduledAt(null);
                  setScheduleEnabled(false);
                }}
              >
                <Icon name="close" size={12} />
              </ChipRemoveBtn>
            </ScheduledChip>
          )}
          {/* 1.23.00 Phase 5: free for everyone — not a Pro gate. Hidden while a
            schedule is set (draft and scheduledAt are mutually exclusive). */}
          {(overlay || !scheduledAt) && (
            <Button
              variant="ghost"
              size="sm"
              disabled={!(overlay ? canSaveDraft : canSubmit)}
              onClick={() => void handlePost(true)}
            >
              Save as draft
            </Button>
          )}
          {!compact && (
            <Button
              variant="primary"
              size="sm"
              disabled={!canSubmit}
              onClick={() => void handlePost()}
            >
              {overlay && <Icon name="send" size={18} />}
              {scheduledAt ? 'Schedule' : 'Post'}
            </Button>
          )}
        </PostRow>
      )}
    </Wrap>
  );
}
